import { useEffect, useMemo, useState } from "react";

import { TilleyIcon } from "components/Icons/Icons";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { useLocalStorage } from "../../core/useLocalStorageForChat";
import { sortChatsByLastMessage } from "../../utility/chatUtils";
import ChatContent from "./ChatContent";

export default function TilleyChatWorkspace() {
  const [chats, setChats] = useLocalStorage("tilley_chat_history", []);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { clearAttachmentsExceptUserProfile } = useChatAssistantAttachments();

  const sortedChats = useMemo(() => sortChatsByLastMessage(chats), [chats]);

  const mostRecentChatId = useMemo(() => {
    return sortedChats.length > 0 ? sortedChats[0].id : null;
  }, [sortedChats]);

  // Clear all attachments except user profile when BIG CHAT workspace loads
  useEffect(() => {
    clearAttachmentsExceptUserProfile();
  }, [clearAttachmentsExceptUserProfile]);

  // Migration effect to ensure all chats have lastMessageDate
  useEffect(() => {
    const needsMigration = chats.some((chat) => !chat.lastMessageDate);
    if (needsMigration) {
      const migratedChats = chats.map((chat) => {
        if (!chat.lastMessageDate) {
          const lastMessage =
            chat.messages && chat.messages.length > 0 ? chat.messages[chat.messages.length - 1] : null;
          const lastMessageDate = lastMessage?.sentTime || chat.createdAt || Date.now();
          return { ...chat, lastMessageDate };
        }
        return chat;
      });
      setChats(migratedChats);
    }
  }, []);

  useEffect(() => {
    if (isInitialLoad && chats.length > 0 && currentChatId === null) {
      setCurrentChatId(mostRecentChatId);
      setIsInitialLoad(false);
      return;
    }

    if (chats.length === 0) {
      // Don't create a default chat automatically - let user start with new chat mode
      setCurrentChatId(null);
    } else if (currentChatId && !chats.some((chat) => chat.id === currentChatId)) {
      // If currentChatId is not null but invalid, find the most recent chat
      setCurrentChatId(mostRecentChatId);
    }
  }, [chats, currentChatId, isInitialLoad, mostRecentChatId]);

  // Determine header title based on current state
  const headerTitle = isSettingsOpen ? "Settings" : "Hey, Tilley chat";

  return (
    <div className="tilley-chat-wrapper">
      <WorkspaceHeader
        category={{
          title: headerTitle,
          icon: <TilleyIcon />,
        }}
        compact
      />
      <div className="tilley-chat-workspace">
        <ChatContent
          chats={chats}
          setChats={setChats}
          currentChatId={currentChatId}
          setCurrentChatId={setCurrentChatId}
          isSettingsOpen={isSettingsOpen}
          setIsSettingsOpen={setIsSettingsOpen}
        />
      </div>
    </div>
  );
}
